import type { Position } from 'geojson';
import { around } from 'geokdbush';
import type { Immutable } from 'immer';
import KDBush from 'kdbush';
import { type DeviceStateFlag, Targets } from '../consts/events';
import { formatJSONTime, formatTimeDuration } from '../formattings/times';
import { formatAsKM } from '../formattings/units';
import { clamp } from '../math';
import { calculateDistanceInMeter, Degrees2radians } from '../point_utils';
import { floorTo } from '../roundTo';
import type { RacemapEvent, Tags } from '../types/events';
import type { SplitObject } from '../types/geos';
import type { LatLng } from '../types/trackping';
import type { PointWithOffset } from '../types/types';
import type { ID } from '../types/utils';

export type { TagsCollection } from '@racemap/sdk/schema/starters';

import { Unit } from '../types/units';
import type { PointWithOffsetTimeNumber, PointWithTrackOffset } from './codec';

export type ExtendedStarterTrack = Array<ExtendedStarterTrackPoint>;

type PointWithTimeOffset = PointWithOffset & {
  timeOffset?: number;
  relativeDelta?: number;
  geofenceId?: string;
  manualDuration?: number;
  distanceToShadowTrack?: number;
};

export type PointWithSpeedAndOffset = PointWithTimeOffset & {
  speed: number; // Its used as meters per millisecond
  forceFinish?: boolean;
  splitTimeError?: number;
  splitOffsetError?: number;
  isBreak?: boolean;
};

export interface RefinedPoint extends Omit<PointWithTrackOffset, 'time'> {
  time: Date;
  speed: number;
  timeOffset: number;
  isBreak: boolean;
}

export type ExtendedStarterTrackPoint = RefinedPoint & {
  distanceToShadowTrack: number;
};

export type Geofence = {
  name: string;
  offset?: number;
  timeOffset?: number;
  id?: string;
  position?: Position;
  thresholds?: Thresholds;
};

type Thresholds = {
  latHigh: number;
  latLow: number;
  lngHigh: number;
  lngLow: number;
};

interface TimeAtGeofence {
  id: string;
  relativeProgress: number;
  time: number;
  relativeDeltaProgress?: number;
}

export interface StarterInfo {
  id: ID;
  name: string;
  trackId: ID;
  startNumber: string;
  currentSpeed: number;
  tags: Tags | null;
  markerColor: string;
  rank: number;
  state: DeviceStateFlag | null;
  progress: number;
  activeDuration: number | null;
  results: Array<TimeAtGeofence>;
  online: boolean;
  manualFinishDuration: number;
  currentDuration: number | null;
}

export interface PreparedStarterInfo extends StarterInfo {
  tagsRank?: Record<string, number>;
  restDuration: number;
}

export interface RanksPackage {
  name: string;
  startTime: string;
  endTime: string;
  geoJSON: string;
  location: string;
  image: string;
  timekeepings: Array<Geofence>;
  starters: Array<StarterInfo>;
}

export interface PreparedRanksPackage extends RanksPackage {
  tagsStarterCount: Map<string, Map<string, number>>;
  starters: Array<PreparedStarterInfo>;
}

export enum RanksQueryFrom {
  MONITOR = 'MONITOR',
  LEADERBOARD = 'LEADERBOARD',
  APP = 'APP',
}

/** Measures the distance in meter from a point to an indexed shadow track. */
export type ShadowTrackDistance = (point: { lat: number; lng: number }) => number;

/**
 * Distance in meter from `point` to the segment `a`-`b`, both in GeoJSON order.
 *
 * Projects onto a plane centred on the point, where a segment of a few tens of
 * meters is straight, clamps to the segment, then measures the closest point
 * back on the sphere so the result stays in the same units as everything else.
 */
function distanceToSegmentInMeter(
  a: LatLng,
  b: LatLng,
  point: { lat: number; lng: number },
): number {
  const cosLat = Math.cos(point.lat * Degrees2radians);
  // Degenerate at the poles, where scaling longitude loses all meaning.
  if (Math.abs(cosLat) < 1e-9) {
    return calculateDistanceInMeter(a[1], a[0], point.lat, point.lng);
  }

  const ax = (a[0] - point.lng) * cosLat;
  const ay = a[1] - point.lat;
  const dx = (b[0] - a[0]) * cosLat;
  const dy = b[1] - a[1];

  const lengthSquared = dx * dx + dy * dy;
  const along = lengthSquared === 0 ? 0 : clamp(0, -(ax * dx + ay * dy) / lengthSquared, 1);

  return calculateDistanceInMeter(
    point.lat + ay + along * dy,
    point.lng + (ax + along * dx) / cosLat,
    point.lat,
    point.lng,
  );
}

/**
 * Indexes a shadow track once and returns the distance in meter from a point to
 * the nearest place *on* the track, not merely to its nearest vertex. Shadow
 * track entries are in GeoJSON order, `[lng, lat]` (a third entry, the
 * elevation, is ignored). An empty shadow track measures 0 for every point, the
 * same as having no shadow track at all.
 *
 * Measuring to the vertex would quantise every reading by the resampling step —
 * the chunked track is spaced 10 m apart, so a runner standing on the route
 * between two vertices would read up to 5 m off it. That is invisible at the
 * hundreds of meters an away-from-track alarm fires at, and the whole signal at
 * the few meters a chart baseline shows.
 *
 * Building the index sorts every vertex, so hold on to the returned measurer for
 * as long as the shadow track itself lives rather than rebuilding it per use.
 */
export function buildShadowTrackDistance(shadowTrack: Array<LatLng>): ShadowTrackDistance {
  if (shadowTrack.length === 0) return () => 0;

  const index = new KDBush(shadowTrack.length);
  for (const [lng, lat] of shadowTrack) {
    index.add(lng, lat);
  }
  index.finish();

  return (point) => {
    const [nearest] = around(index, point.lng, point.lat, 1);
    const [lng, lat] = shadowTrack[nearest];
    let distance = calculateDistanceInMeter(lat, lng, point.lat, point.lng);

    // The closest place on the track lies on one of the two segments meeting at
    // the closest vertex, so only those two need measuring.
    if (nearest > 0) {
      distance = Math.min(
        distance,
        distanceToSegmentInMeter(shadowTrack[nearest - 1], shadowTrack[nearest], point),
      );
    }
    if (nearest < shadowTrack.length - 1) {
      distance = Math.min(
        distance,
        distanceToSegmentInMeter(shadowTrack[nearest], shadowTrack[nearest + 1], point),
      );
    }
    return distance;
  };
}

export function sortTimes(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null || Number.isNaN(a)) {
    if (b == null || Number.isNaN(b)) {
      return 0;
    }
    return 1;
  }
  if (b == null || Number.isNaN(b)) {
    return -1;
  }
  return a - b;
}

export function eventHasVirtDuration(event: Immutable<RacemapEvent>) {
  return (
    (!eventHasShadowtrack(event) || event.modules.projection.enabled) &&
    event.modules.timing.target === Targets.DURATION &&
    event.modules.timing.duration != null
  );
}

export function eventHasVirtDistance(event: Immutable<RacemapEvent>) {
  return (
    (!eventHasShadowtrack(event) || event.modules.projection.enabled) &&
    event.modules.timing.target === Targets.DISTANCE &&
    event.modules.timing.distance != null
  );
}

export function getVirtualRaceDistance(
  event: Immutable<RacemapEvent>,
  splits: Immutable<Array<SplitObject>>,
): number {
  const timekeepingSplits = splits?.filter((split) => split.properties.timekeeping);
  const withShadowtrack = eventHasShadowtrack(event);

  if (withShadowtrack && timekeepingSplits?.length > 0) {
    const lastTimekeepingSplit = timekeepingSplits?.[timekeepingSplits.length - 1];
    const firstTimekeepingSplit = timekeepingSplits?.[0];

    return (
      (lastTimekeepingSplit.properties.offset || 0) - (firstTimekeepingSplit.properties.offset || 0)
    );
  }

  if (withShadowtrack && splits.length > 0) {
    const lastSplit = splits[splits.length - 1];
    if (lastSplit.properties.offset == null) throw new Error("Offset shouldn't be null");

    return lastSplit.properties.offset;
  }

  return event.modules.timing.distance;
}

export function getVirtualRaceDuration(event: Immutable<RacemapEvent>) {
  return event?.modules?.timing?.duration;
}

export function eventHasShadowtrack(event: Immutable<RacemapEvent>) {
  return event.geo != null && event.geo.shadowgeojsonHash != null;
}

export function buildCSVFile(
  tableData: Array<StarterInfo | null>,
  geofences: Array<Geofence>,
  eventHasVirtDuration: boolean,
) {
  return new Blob(
    [
      Array.from(
        (function* () {
          yield `ID;Name;Start Number;Rank;Current Progress (${
            eventHasVirtDuration ? 'hh:mm:ss' : Unit.Distance.km
          });${geofences
            .map(
              (g) =>
                `${g.name} (${
                  eventHasVirtDuration
                    ? formatTimeDuration(g.timeOffset)
                    : formatAsKM({ meters: g.offset })
                }) Split;${g.name} (${formatAsKM({ meters: g.offset })}) Timestamp`,
            )
            .join(';')}`;
          for (const starterData of tableData) {
            if (starterData == null) continue;
            yield [
              starterData.id,
              starterData.name,
              starterData.startNumber,
              starterData.rank,
              floorTo(starterData.progress / 1000, 2),
              ...starterData.results
                .slice(0)
                .map((time) =>
                  time != null
                    ? `${
                        eventHasVirtDuration
                          ? formatAsKM({ meters: time.relativeProgress })
                          : formatTimeDuration(time.relativeProgress)
                      };${formatJSONTime(time.time) || ''}`
                    : ';',
                ),
            ].join(';');
          }
        })(),
      ).join('\n'),
    ],
    {
      type: 'text/csv;charset=utf-8;',
    },
  );
}

export function buildGPXFile(pointsMap: Record<string, Array<PointWithOffsetTimeNumber>>): Blob {
  let tracks = '';

  for (const [eventName, points] of Object.entries(pointsMap)) {
    tracks += `<trk>
    <name>${eventName}</name>
    <trkseg>${points
      .map(
        (p) => `
      <trkpt lat="${p.lat}" lon="${p.lng}">
        <time>${new Date(p.time).toISOString()}</time>
      </trkpt>`,
      )
      .join('')}
    </trkseg>
  </trk>`;
  }

  return new Blob(
    [
      `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<gpx version="1.1" creator="Racemap">
  <metadata><time>${new Date().toISOString()}</time></metadata>
  ${tracks}
</gpx>
`,
    ],
    { type: 'application/gpx+xml' },
  );
}
